// @flow

import React from 'react';
import {
  Card,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { BadgeStatus } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { FormattedMessage } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';
import messages from './messages';

type Props = {
  testId: string,
  name: MessageDescriptor,
  description: MessageDescriptor,
  enabled: boolean,
};

/**
 * One assistant in the AI Assistants area, with its property-level status.
 *
 * Read-only by construction rather than by a disabled control: there is no button, no switch
 * and no click target, because the property-level configuration is held in the database and
 * nothing in Fortress can change it. A disabled toggle would advertise a capability the
 * screen does not have.
 *
 * The status is the shared BadgeStatus chip - `successful` (green) when the assistant is on,
 * `error` (red) when off - so the colours are the component library's own success/error tokens
 * rather than values pinned here, and the same chip reads the same way everywhere it appears.
 */
const AssistantCard = ({ testId, name, description, enabled }: Props) => (
  <Card data-testid={`${testId}-assistant-card`} sx={{ padding: 2 }}>
    <Stack spacing={1} alignItems="flex-start">
      <Typography variant="titleSmall" component="h2">
        <FormattedMessage {...name} />
      </Typography>
      <Typography variant="bodySmall">
        <FormattedMessage {...description} />
      </Typography>
      <BadgeStatus
        data-testid={`${testId}-assistant-status`}
        status={enabled ? 'successful' : 'error'}
        variant="filled"
        size="small"
      >
        <FormattedMessage {...(enabled ? messages.on : messages.off)} />
      </BadgeStatus>
    </Stack>
  </Card>
);

export default AssistantCard;
